import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useGameState } from '../hooks/useGameState';
import { DEFAULT_SETTINGS } from '../utils/gameLogic';

const GameBoardContainer = styled.div`
  width: 100%;
  max-width: 600px;
  height: 400px;
  margin: 0 auto;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
`;

interface ClickAreaProps {
  $isReady: boolean;
  $status: string;
}

const ClickArea = styled.div<ClickAreaProps>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.$isReady ? 'pointer' : 'default'};
  background: ${props => {
    if (props.$status === 'waiting') return '#ff9800';
    if (props.$status === 'ready') return '#4CAF50';
    return '#f0f0f0';
  }};
  border: 3px solid ${props => {
    if (props.$status === 'waiting') return '#e68900';
    if (props.$status === 'ready') return '#45a049';
    return '#ddd';
  }};
  border-radius: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => {
      if (props.$status === 'waiting') return '#e68900';
      if (props.$status === 'ready') return '#45a049';
      return '#e0e0e0';
    }};
  }
`;

const StatusText = styled(motion.div)`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.color || '#333'};
  text-align: center;
`;

const GameInfo = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  font-size: 1rem;
  color: #666;
`;

const GameStatus = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 0.9rem;
  color: #888;
  background: rgba(255, 255, 255, 0.9);
  padding: 5px 10px;
  border-radius: 15px;
`;

interface GameBoardProps {
  onRoundComplete: (reactionTime: number, score: number) => void;
}

interface GameBoardProps {
  onRoundComplete: (reactionTime: number, score: number) => void;
  onGameComplete: (bestReactionTime: number, finalScore: number) => void;
  gameStatus: string;
  gameRound: number;
  gameScore: number;
  onGameStart: () => void;
}

const GameBoard: React.FC<GameBoardProps> = ({ 
  onRoundComplete, 
  onGameComplete,
  gameStatus, 
  gameRound, 
  gameScore,
  onGameStart 
}) => {
  const { 
    status, 
    reactionTime, 
    currentScore, 
    round, 
    startGame, 
    handleClick, 
    finishRound,
    showSignal,
    roundReactionTimes,
    getBestReactionTime,
    isGameComplete
  } = useGameState();

  // 디버깅용 로그
  console.log('GameBoard - status:', status, 'round:', round, 'gameStatus:', gameStatus);

  const handleAreaClick = () => {
    if (status === 'ready') {
      handleClick();
    } else if (status === 'waiting') {
      // 너무 일찍 클릭한 경우 (예외 처리)
      console.log('너무 일찍 클릭했습니다!');
    }
  };

  // 게임 시작 시 자동으로 startGame 호출
  React.useEffect(() => {
    if (status === 'idle' && gameStatus === 'waiting') {
      startGame();
    }
  }, [status, gameStatus, startGame]);

  // 라운드가 0으로 초기화되면 게임 시작 (다시 시작하기 경우)
  React.useEffect(() => {
    if (round === 0 && gameStatus === 'waiting') {
      setTimeout(() => {
        startGame();
      }, 50);
    }
  }, [round, gameStatus, startGame]);

  // waiting 상태일 때 랜덤 지연 후 showSignal 호출
  React.useEffect(() => {
    if (status === 'waiting') {
      const delay = Math.floor(Math.random() * 3000) + 1000; // 1-4초
      console.log('Setting timer for', delay, 'ms');
      const timer = setTimeout(() => {
        console.log('Timer expired, calling showSignal');
        showSignal();
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [status, showSignal]);

  // status가 finished이고 reactionTime이 있을 때 자동으로 처리
  React.useEffect(() => {
    // 새 게임인지 확인 (round가 1이고 roundReactionTimes가 비어있으면 새 게임)
    const isNewGame = round === 1 && roundReactionTimes.length === 0;
    
    if (status === 'finished' && reactionTime !== null && !isNewGame) {
      const timeout = setTimeout(() => {
        onRoundComplete(reactionTime, currentScore);
        
        // 게임이 완료되었는지 확인
        if (isGameComplete()) {
          const bestTime = getBestReactionTime();
          if (bestTime !== null) {
            onGameComplete(bestTime, currentScore);
          }
        } else {
          finishRound();
        }
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [status, reactionTime, currentScore, onRoundComplete, finishRound, isGameComplete, getBestReactionTime, onGameComplete, round, roundReactionTimes.length]);

  // 새 게임 시작 시 이전 상태 정리
  React.useEffect(() => {
    if (round === 1 && status === 'waiting' && roundReactionTimes.length === 0) {
      console.log('New game started, clearing previous state');
    }
  }, [round, status, roundReactionTimes.length]);

  const getStatusText = () => {
    switch (status) {
      case 'idle':
        return '게임을 시작해보세요!';
      case 'waiting':
        return '초록색이 되면 클릭하세요...';
      case 'ready':
        return '지금 클릭!';
      case 'finished':
        return reactionTime ? `${reactionTime}ms - 완료!` : '라운드 종료';
      default:
        return '';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'idle':
        return '#666';
      case 'waiting':
        return '#ff9800';
      case 'ready':
        return '#fff';
      case 'finished':
        return reactionTime ? '#4CAF50' : '#f44336';
      default:
        return '#333';
    }
  };

  return (
    <GameBoardContainer>
      <GameInfo>
        라운드 {round}/{DEFAULT_SETTINGS.maxRounds} | 점수: {currentScore}
      </GameInfo>
      
      {roundReactionTimes.length > 0 && (
        <GameStatus>
          기록: {roundReactionTimes.map((time, i) => `${time}ms`).join(', ')}
        </GameStatus>
      )}
      
      <ClickArea 
        $isReady={status === 'ready'}
        $status={status}
        onClick={handleAreaClick}
      >
        <StatusText
          color={getStatusColor()}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          {getStatusText()}
        </StatusText>
      </ClickArea>
    </GameBoardContainer>
  );
};

export default GameBoard;